import './EditFeatureFlagsModal.scss'
import {i18n} from "../../../../Utils/i18n/i18n";
import {Button} from "../../../Form/Button/Button";
import {buttonColors} from "../../../Pickers/InputTheme";
import {Modal} from "../../../Smart/Modal/Modal";
import React, {useEffect, useState} from "react";
import {SA_getCompanyFeatureFlags, SA_updateCompanyFeatureFlags} from "../../../../Services/SA_api";
import {FormControlLabel, Switch, Accordion, AccordionSummary, AccordionDetails} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {FeatureFlags} from "../../../../Utils/Constants";
import LoadingSpinner from "../../../LoadingSpinner/LoadingSpinner";

/**
 * Purchase path tiers - Groups feature flags by purchase path (Basic, Creator, Enterprise).
 * Each path contains deals (type-based groupings) with their feature flags.
 */
const FFPaths = {
    basic: {
        key: 'basic',
        deals: {
            messaging: {
                key: 'messaging',
                flags: [
                    FeatureFlags.SMS,
                    FeatureFlags.EMAIL,
                    FeatureFlags.WHATSAPP,
                    FeatureFlags.SEND_WELCOME_MESSAGE,
                    FeatureFlags.NOTIFY_NEW_TASK_TO_USER
                ]
            },
            automation: {
                key: 'automation',
                flags: [
                    FeatureFlags.AUTOMATION,
                    FeatureFlags.RECURRING_AUTOMATION
                ]
            },
            contentTypes: {
                key: 'contentTypes',
                flags: [FeatureFlags.SIGN_DOC_REGULAR_COURSE]
            },
            organization: {
                key: 'organization',
                flags: [
                    FeatureFlags.ORG_ADMIN_MGMT,
                    FeatureFlags.SUB_ADMIN_CAN_CREATE_ORG
                ]
            },
            system: {
                key: 'system',
                flags: [FeatureFlags.LANGUAGES]
            }
        }
    },
    creator: {
        key: 'creator',
        deals: {
            contentTypes: {
                key: 'contentTypes',
                flags: [
                    FeatureFlags.SIGN_DOCS_READ_AND_SIGN_DOC_PDF,
                    FeatureFlags.SIGN_DOCS_WATCH_AND_SIGN_VIDEO_FILE,
                    FeatureFlags.SIGN_DOCS_WATCH_AND_SIGN_VIMEO_LINK,
                    FeatureFlags.SIGN_DOCS_WATCH_AND_SIGN_YOUTUBE_LINK,
                    FeatureFlags.SIGN_DOCS_AUDIO_FILE,
                    FeatureFlags.SIGN_DOC_CANVA_PRESENTATION,
                    FeatureFlags.LINK,
                    FeatureFlags.EXAM,
                    FeatureFlags.LEARNING_PATH
                ]
            }
        }
    },
    enterprise: {
        key: 'enterprise',
        deals: {
            contentTypes: {
                key: 'contentTypes',
                flags: [
                    FeatureFlags.FRONTAL_COURSE,
                    FeatureFlags.ONLINE_COURSE,
                    FeatureFlags.UPLOAD_DOCUMENT,
                    FeatureFlags.SURVEYS
                ]
            },
            advanced: {
                key: 'advanced',
                flags: [
                    FeatureFlags.AI,
                    FeatureFlags.KNOWLEDGE_CENTER,
                    FeatureFlags.CERTIFICATION
                ]
            },
        }
    }
}

/**
 * Standalone feature flags not belonging to any purchase path
 */
const STANDALONE_FLAGS = [
    FeatureFlags.OPERATION_MODE,
    FeatureFlags.MUST_DO_COMPLIANCE,
    FeatureFlags.WEEZBEE_COURSE,
    FeatureFlags.BULK_USER_REFACTOR
]

/**
 * Feature flags that are always toggled together.
 * When BULK_USER_REFACTOR is toggled, PHONE_E164_MIGRATION is also set to the same value.
 * This ensures phone number migration and bulk user refactor are enabled/disabled in sync.
 */
const LINKED_FEATURE_FLAGS = {
    [FeatureFlags.BULK_USER_REFACTOR]: [FeatureFlags.PHONE_E164_MIGRATION]
};

/**
 * Gets all linked flags for a given flag (recursively)
 */
const getLinkedFlags = (flag) => {
    return LINKED_FEATURE_FLAGS[flag] || [];
};

export const EditFeatureFlagsModal = ({editFFModalOpen, closeEditFFModal, customerId}) => {
    const [loading, setLoading] = useState(true)
    const [featureFlags, setFeatureFlags] = useState({})
    const [expandedPaths, setExpandedPaths] = useState({})
    const [expandedDeals, setExpandedDeals] = useState({})

    /**
     * Gets all flags across all deals in a path
     * @param {Object} path - Path object containing deals
     * @returns {string[]} Array of all flag keys in the path
     */
    const getAllPathFlags = (path) => {
        return Object.values(path.deals).flatMap(deal => deal.flags)
    }

    /**
     * Checks if all flags in a deal are enabled
     * @param {Object} deal - Deal object with flags array
     * @returns {boolean}
     */
    const isDealFullyEnabled = (deal) => {
        return deal.flags.every(flag => featureFlags[flag])
    };

    /**
     * Checks if some (but not all) flags in a deal are enabled
     * @param {Object} deal - Deal object with flags array
     * @returns {boolean}
     */
    const isDealPartiallyEnabled = (deal) => {
        return deal.flags.some(flag => featureFlags[flag]) && !isDealFullyEnabled(deal)
    };

    /**
     * Checks if all flags across all deals in a path are enabled
     * @param {Object} path - Path object containing deals
     * @returns {boolean}
     */
    const isPathFullyEnabled = (path) => {
        return getAllPathFlags(path).every(flag => featureFlags[flag])
    };

    /**
     * Checks if some (but not all) flags across a path are enabled
     * @param {Object} path - Path object containing deals
     * @returns {boolean}
     */
    const isPathPartiallyEnabled = (path) => {
        const allFlags = getAllPathFlags(path)
        return allFlags.some(flag => featureFlags[flag]) && !isPathFullyEnabled(path)
    };

    /**
     * Toggle a single feature flag
     * @param {string} key - Feature flag key
     * @returns {Function} Async click handler
     */
    const onFFChange = (key) => async () => {
        try {
            const linkedFlags = getLinkedFlags(key);
            let updatedFlags;

            setFeatureFlags(currentFlags => {
                const newValue = !currentFlags[key];
                const flagsToUpdate = {[key]: newValue};

                linkedFlags.forEach((k) => {
                    flagsToUpdate[k] = newValue
                });

                updatedFlags = {...currentFlags, ...flagsToUpdate};
                return updatedFlags;
            });

            const newFF = await SA_updateCompanyFeatureFlags({
                companyId: customerId,
                featureFlags: updatedFlags
            })
            if (newFF && !newFF.error) {
                setFeatureFlags(newFF)
            }
        } catch (error) {
            console.error('Failed to update feature flag:', error)
        }
    }

    /**
     * Toggle all feature flags in a deal at once
     * @param {Object} deal - Deal object with flags array
     * @param {string} compositeKey - Unique key for this deal (pathKey-dealKey)
     * @returns {Function} Async click handler
     */
    const onDealChange = ({deal}) => async () => {
        try {
            let updatedFlags;

            setFeatureFlags(currentFlags => {
                const hasAnyEnabled = deal.flags.some(flag => currentFlags[flag]);
                const newValue = !hasAnyEnabled;
                let flagsToUpdate = {};

                deal.flags.forEach((flag) => {
                    flagsToUpdate[flag] = newValue;
                    const linkedFlags = getLinkedFlags(flag);
                    linkedFlags.forEach((k) => {
                        flagsToUpdate[k] = newValue
                    });
                });

                updatedFlags = {...currentFlags, ...flagsToUpdate};
                return updatedFlags;
            });

            const newFF = await SA_updateCompanyFeatureFlags({
                companyId: customerId,
                featureFlags: updatedFlags
            })
            if (newFF && !newFF.error) {
                setFeatureFlags(newFF)
            }
        } catch (error) {
            console.error('Failed to update deal flags:', error)
        }
    }

    /**
     * Toggle all feature flags across all deals in a path
     * @param {Object} path - Path object containing deals
     * @returns {Function} Async click handler
     */
    const onPathChange = (path) => async () => {
        try {
            let updatedFlags;

            setFeatureFlags(currentFlags => {
                const allPathFlags = getAllPathFlags(path);
                const hasAnyEnabled = allPathFlags.some(flag => currentFlags[flag]);
                const newValue = !hasAnyEnabled;
                let flagsToUpdate = {};

                allPathFlags.forEach((flag) => {
                    flagsToUpdate[flag] = newValue;
                    const linkedFlags = getLinkedFlags(flag);
                    linkedFlags.forEach((k) => {
                        flagsToUpdate[k] = newValue
                    });
                });

                updatedFlags = {...currentFlags, ...flagsToUpdate};
                return updatedFlags;
            });

            const newFF = await SA_updateCompanyFeatureFlags({
                companyId: customerId,
                featureFlags: updatedFlags
            })
            if (newFF && !newFF.error) {
                setFeatureFlags(newFF)
            }
        } catch (error) {
            console.error('Failed to update path flags:', error)
        }
    }

    /**
     * @param {string} pathKey - Path key for accordion state
     * @returns {Function} Accordion change handler
     */
    const handlePathAccordionChange = (pathKey) => (event, isExpanded) => {
        setExpandedPaths(prev => ({...prev, [pathKey]: isExpanded}))
    }

    /**
     * @param {string} compositeKey - Composite key (pathKey-dealKey) for accordion state
     * @returns {Function} Accordion change handler
     */
    const handleDealAccordionChange = (compositeKey) => (event, isExpanded) => {
        setExpandedDeals(prev => ({...prev, [compositeKey]: isExpanded}))
    }

    useEffect(() => {
        const fetchFeatureFlags = async () => {
            setLoading(true)
            const ffs = await SA_getCompanyFeatureFlags({companyId: customerId})
            setFeatureFlags(ffs)
            setLoading(false)
        }
        fetchFeatureFlags()
    }, [customerId])

    return <Modal
        open={editFFModalOpen}
        onClose={closeEditFFModal}>
        <div className="SuperAdminEditFFModal modalWrapper">
            <div className="modalTitle">
                <div>{i18n.translate('superAdmin.editFFModal.modalTitle')}</div>
            </div>
            <div className="modalContent">
                {loading
                    ? <LoadingSpinner local/>
                    : <>
                        {Object.values(FFPaths).map((path) => {
                            const pathFullyEnabled = isPathFullyEnabled(path);
                            const pathPartiallyEnabled = isPathPartiallyEnabled(path);

                            return <Accordion
                                key={path.key}
                                expanded={expandedPaths[path.key] || false}
                                onChange={handlePathAccordionChange(path.key)}
                                className={`ffPathAccordion ${pathFullyEnabled ? 'path-enabled' : ''} ${pathPartiallyEnabled ? 'path-partial' : ''}`}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    className="ffPathHeader"
                                >
                                    <div className="ffToggleWrapper" onClick={(e) => e.stopPropagation()}>
                                        <Switch
                                            checked={pathFullyEnabled || pathPartiallyEnabled}
                                            onChange={onPathChange(path)}
                                        />
                                    </div>
                                    <span className="ffLabel">{i18n.translate(`superAdmin.editFFModal.paths.${path.key}`)}</span>
                                    <span className="pathStatus">
                                        {pathFullyEnabled
                                            ? i18n.translate('superAdmin.editFFModal.dealStatus.allEnabled')
                                            : pathPartiallyEnabled
                                                ? i18n.translate('superAdmin.editFFModal.dealStatus.partiallyEnabled')
                                                : i18n.translate('superAdmin.editFFModal.dealStatus.disabled')
                                        }
                                    </span>
                                </AccordionSummary>
                                <AccordionDetails className="ffPathContent">
                                    {Object.values(path.deals).map((deal) => {
                                        const dealFullyEnabled = isDealFullyEnabled(deal);
                                        const dealPartiallyEnabled = isDealPartiallyEnabled(deal);
                                        const compositeKey = `${path.key}-${deal.key}`;

                                        return <Accordion
                                            key={compositeKey}
                                            expanded={expandedDeals[compositeKey] || false}
                                            onChange={handleDealAccordionChange(compositeKey)}
                                            className={`ffDealAccordion ${dealFullyEnabled ? 'deal-enabled' : ''} ${dealPartiallyEnabled ? 'deal-partial' : ''}`}
                                        >
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                className="ffDealHeader"
                                            >
                                                <div className="ffToggleWrapper" onClick={(e) => e.stopPropagation()}>
                                                    <Switch
                                                        checked={dealFullyEnabled || dealPartiallyEnabled}
                                                        onChange={onDealChange({deal})}
                                                    />
                                                </div>
                                                <span className="ffLabel">{i18n.translate(`superAdmin.editFFModal.deals.${deal.key}`)}</span>
                                                <span className="dealStatus">
                                                    {dealFullyEnabled
                                                        ? i18n.translate('superAdmin.editFFModal.dealStatus.allEnabled')
                                                        : dealPartiallyEnabled
                                                            ? i18n.translate('superAdmin.editFFModal.dealStatus.partiallyEnabled')
                                                            : i18n.translate('superAdmin.editFFModal.dealStatus.disabled')
                                                    }
                                                </span>
                                            </AccordionSummary>
                                            <AccordionDetails className="ffDealContent">
                                                {deal.flags.map((flag) => (
                                                    <FormControlLabel
                                                        key={flag}
                                                        control={<Switch checked={featureFlags[flag] || false} onChange={onFFChange(flag)}/>}
                                                        label={i18n.translate(`superAdmin.editFFModal.featureFlags.${flag}`)}
                                                    />
                                                ))}
                                            </AccordionDetails>
                                        </Accordion>
                                    })}
                                </AccordionDetails>
                            </Accordion>
                        })}
                        <div className="ffStandaloneSection">
                            {STANDALONE_FLAGS.map((flag) => (
                                <FormControlLabel
                                    key={flag}
                                    control={<Switch checked={featureFlags[flag] || false} onChange={onFFChange(flag)}/>}
                                    label={i18n.translate(`superAdmin.editFFModal.featureFlags.${flag}`)}
                                />
                            ))}
                        </div>
                    </>
                }
            </div>
            <div className="modalFooter">
                <Button
                    color={buttonColors.GREY}
                    onClick={closeEditFFModal}
                    text={i18n.translate('general.close')}
                />
            </div>
        </div>
    </Modal>
}